import * as React from 'react';
import Pluralize from 'pluralize';

import activityFromClassificationId from '../../modules/activity_from_classification_id.js';
import useScaledTableColumnWidths from '../../../../Shared/hooks/useScaledTableColumnWidths';
import { DataTable, Tooltip, EXTRA_SMALL_ICON_BASE_SRC } from '../../../../Shared/index';
import { nonRelevantActivityClassificationIds } from '../../../../../modules/activity_classifications';
import { requestGet } from '../../../../../modules/request';

const chevronRightImgSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/chevron-right.svg`;

interface Classroom {
  assignedStudentCount: number;
  name: string;
  totalStudentCount: number;
}

interface ClassroomActivity {
  activityClassificationId: number;
  activityId: number;
  completedCount: number;
  cumulativeScore: number;
  name: string;
}

interface ActivityPackProps {
  data: {
    unitId: number;
    unitName: string;
    classrooms: Classroom[];
    classroomActivities: Map<number, ClassroomActivity>;
  };
}

const ActivityPack: React.FC<ActivityPackProps> = ({ data }) => {
  const containerRef = React.useRef<HTMLElement>(null);

  const columnWidths = useScaledTableColumnWidths({
    additionalWidthRestrictions: 2,
    columnWidths: [
      { minWidth: 500, width: 720 },
      { minWidth: 150, width: 230 },
      { minWidth: 100, width: 230 },
    ],
    containerRef,
    maxTableWidth: 1198,
    minTableWidth: 750,
  });

  const headers = [
    {
      attribute: 'tool',
      name: 'Tool',
      renderCellsAsHeaders: true,
      width: columnWidths[0],
    },
    {
      attribute: 'completion',
      name: 'Completion',
      width: columnWidths[1],
    },
    {
      attribute: 'averageScore',
      name: 'Average score',
      rowSectionClassName: 'average-score',
      width: columnWidths[2],
    },
  ];

  const { unitName, classroomActivities, unitId, classrooms } = data;

  const numberOfStudentsAssignedToUnit = () =>
    classrooms.reduce((sum, c) => sum + c.assignedStudentCount, 0);

  const handleReportLinkClick = (unitId: number, activityId: number) => {
    requestGet(
      `/teachers/progress_reports/report_from_unit_and_activity/u/${unitId}/a/${activityId}`,
      (data) => {
        window.location.href = data.url;
      },
      () =>
        alert(
          'This report is not yet available. Please check back when at least one of your students has completed this activity.'
        )
    );
  };

  const renderAssignedToSection = () => (
    <div className="assigned-to">
      <span>
        Assigned to {classrooms.length} {Pluralize('class', classrooms.length)}:
      </span>
      <ul>
        {classrooms.map((c, i) => (
          <li key={i}>
            <b>{c.name}</b>
            <span>
              ({c.assignedStudentCount}/{c.totalStudentCount}{' '}
              {Pluralize('student', c.totalStudentCount)})
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderPieChart = (completedCount: number) => {
    const percent = Math.min(completedCount / numberOfStudentsAssignedToUnit(), 1);
    const largeArcFlag = percent > 0.5 ? 1 : 0;
    const angle = 2 * Math.PI * percent;
    const x = Math.cos(angle);
    const y = Math.sin(angle);
    const pathData = `M 1 0 A 1 1 0 ${largeArcFlag} 1 ${x} ${y} L 0 0`;

    return (
      <svg className="activity-analysis-pie-chart" viewBox="-1 -1 2 2">
        <path d={pathData} fill="#348fdf" />
      </svg>
    );
  };

  const calculateAverageScore = (
    cumulativeScore: number,
    completedCount: number,
    activityClassificationId: number
  ) => {
    const averageScore = cumulativeScore / completedCount;

    if (isNaN(averageScore)) {
      return '—';
    } else if (nonRelevantActivityClassificationIds.includes(activityClassificationId)) {
      return (
        <Tooltip tooltipText="This type of activity is not graded." tooltipTriggerText="N/A" />
      );
    } else if (Math.round(averageScore).toString().length === 2) {
      return `${averageScore.toPrecision(2)}%`;
    }

    return `${averageScore}%`;
  };

  const rows = () =>
    Array.from(classroomActivities.values()).map((ca) => {
      const {
        activityClassificationId,
        name,
        completedCount,
        cumulativeScore,
        activityId,
      } = ca;

      const handleClickForReport = () => handleReportLinkClick(unitId, activityId);

      return {
        averageScore: (
          <React.Fragment>
            {calculateAverageScore(cumulativeScore, completedCount, activityClassificationId)}
            <button
              aria-label={`View report for ${name}`}
              className="interactive-wrapper focus-on-light"
              onClick={handleClickForReport}
              type="button"
            >
              <img alt="" src={chevronRightImgSrc} />
            </button>
          </React.Fragment>
        ),
        completion: (
          <React.Fragment>
            {renderPieChart(completedCount)}
            {completedCount} of {numberOfStudentsAssignedToUnit()}{' '}
            {Pluralize('student', numberOfStudentsAssignedToUnit())}
          </React.Fragment>
        ),
        id: activityId,
        tool: (
          <React.Fragment>
            <div className={`icon-wrapper icon-${activityFromClassificationId(activityClassificationId)}-green-no-border`} />
            <span className="activity-name">{name}</span>
          </React.Fragment>
        ),
      };
    });

  if (classroomActivities.size === 0) return null;

  return (
    <section className="activity-analysis-activity-pack" ref={containerRef}>
      <div className="activity-analysis-activity-pack-header">
        <h2>
          <span>Activity pack:</span>
          <b>{unitName}</b>
        </h2>
        {renderAssignedToSection()}
      </div>
      <DataTable headers={headers} reportingFormat rows={rows()} />
    </section>
  );
};

export default ActivityPack;
